import React, { useMemo } from 'react';
import {
  List,
  TextField,
  NumberField,
  DateField,
  DateInput,
  FunctionField,
  ReferenceInput,
  SelectInput,
  TopToolbar,
  useListContext,
  ExportButton
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import jsonExport from 'jsonexport/dist';
import dayjs from 'dayjs';

import FlexibleListLayout from '../FlexibleListLayout';
import YenField from '../YenField';
import downloadCSVCompat from '../../utils/downloadCSVCompat';
import { formatRFC3339DateOnlyString } from '../../utils/dateutils';
import { MAX_PER_PAGE } from '../../constants/define';

export type BuyerDashboardType = 'admin' | 'client' | 'partner';

export interface BuyerListProps {
  dashboardType: BuyerDashboardType;
}

interface BuyerRecord {
  id: string;
  fuid: string;
  no: number;
  clientId: number;
  clientName?: string;
  affiliateId?: string;
  partnerName?: string;
  ecSiteClientId?: string | null;
  rewardPeriodDisplay?: string;
  paymentStartDate?: string | Date;
  paymentEndDate?: string | Date | null;
  purchaseCount?: number;
  totalSales?: number;
  totalReward?: number;
  status?: 'active' | 'inactive';
}

const PER_PAGE_OPTIONS = [5, 10, 25, 50];
const MAX_EXPORT = 1000;

/**
 * 中央寄せのハイフンを表示する共通コンポーネント。
 * データが空の場合の表示用。
 */
export const CenteredHyphen: React.FC = () => (
  <Box component="div" sx={{ textAlign: 'center', width: '100%' }}>
    -
  </Box>
);

/**
 * 購入者管理一覧の共通コンポーネント。
 *
 * - `dashboardType` プロップでカラム/フィルタを切り替える
 * - 行 key は `record.id` = fuid（UUID 文字列）。「No」カラムは `no` フィールド（BIGSERIAL）
 * - 行クリックで `/buyers/<fuid>` に遷移（React Admin 標準 rowClick）
 */
const BuyerList: React.FC<BuyerListProps> = ({ dashboardType }) => {
  const showClientColumns = dashboardType === 'admin'; // successfee のみショップID/ショップ名
  const showClientNameOnly = dashboardType === 'partner'; // partner はショップ名のみ
  const showPartnerColumn = dashboardType !== 'partner'; // partner ダッシュボードはパートナー名非表示
  const showEcSiteClientId = dashboardType !== 'partner';

  const filters = buildFilters(dashboardType);

  const handleExport = async (records: BuyerRecord[]) => {
    if (!records || records.length === 0) return;

    const exportFields: Array<keyof BuyerRecord> = [
      'no',
      'fuid',
      'clientId',
      'clientName',
      'affiliateId',
      'partnerName',
      'ecSiteClientId',
      'rewardPeriodDisplay',
      'paymentStartDate',
      'paymentEndDate',
      'purchaseCount',
      'totalSales',
      'totalReward',
      'status'
    ];
    const headers: string[] = [
      'No',
      'successfee購入者ID',
      'ショップID',
      'ショップ名',
      'アフィリエイトID',
      'パートナー名',
      'ECサイト顧客ID',
      '有効期間',
      '支払期間開始日',
      '支払期間終了日',
      '購入回数',
      '累積売上',
      '累積報酬額',
      'ステータス'
    ];

    // ダッシュボード別の非表示カラムを除外
    const excludedFields = computeExcludedExportFields(dashboardType);
    const filteredFields: Array<keyof BuyerRecord> = [];
    const filteredHeaders: string[] = [];
    exportFields.forEach((field, idx) => {
      if (excludedFields.has(field)) return;
      filteredFields.push(field);
      filteredHeaders.push(headers[idx]);
    });

    const dataToExport = records.map((record) => {
      const row: Record<string, unknown> = {};
      filteredFields.forEach((field, idx) => {
        let value: unknown = record[field];
        if ((field === 'paymentStartDate' || field === 'paymentEndDate') && value) {
          value = formatRFC3339DateOnlyString(value as string | Date);
        } else if (field === 'paymentEndDate' && !value) {
          value = '';
        } else if (field === 'status') {
          value = value === 'active' ? '有効' : value === 'inactive' ? '無効' : '';
        } else if ((field === 'totalSales' || field === 'totalReward') && value !== null && value !== undefined) {
          value = (value as number).toLocaleString();
        }
        row[filteredHeaders[idx]] = value ?? '';
      });
      return row;
    });

    jsonExport(dataToExport, { headers: filteredHeaders }, (err: Error | null, csv: string) => {
      if (err) {
        console.error(err);
        return;
      }
      const bom = '﻿';
      downloadCSVCompat(bom + csv, '購入者管理');
    });
  };

  const filterDefaultValues = useMemo(() => ({
    startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  }), []);

  const BuyerListActions = () => {
    const { total } = useListContext();
    return (
      <TopToolbar>
        <ExportButton label="CSVダウンロード" exporter={handleExport} disabled={total === 0} maxResults={MAX_EXPORT} />
      </TopToolbar>
    );
  };

  return (
    <List
      resource="buyers"
      sort={{ field: 'no', order: 'ASC' }}
      perPage={10}
      filters={filters}
      filterDefaultValues={filterDefaultValues}
      actions={<BuyerListActions />}
      exporter={handleExport}
      empty={
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            購入者はいません。
          </Typography>
        </Box>
      }
    >
      <FlexibleListLayout
        bulkActionButtons={false}
        rowClick={(id: string | number) => `/buyers/${id}`}
      >
        <NumberField source="no" label="No" sortBy="no" textAlign="left" />
        <TextField source="fuid" label="successfee購入者ID" />
        {showClientColumns && <TextField source="clientId" label="ショップID" />}
        {(showClientColumns || showClientNameOnly) && <TextField source="clientName" label="ショップ名" />}
        <TextField source="affiliateId" label="アフィリエイトID" />
        {showPartnerColumn && <TextField source="partnerName" label="パートナー名" />}
        {showEcSiteClientId && (
          <FunctionField
            source="ecSiteClientId"
            label="ECサイト顧客ID"
            render={(record: BuyerRecord) =>
              record.ecSiteClientId ? record.ecSiteClientId : <CenteredHyphen />
            }
          />
        )}
        <TextField source="rewardPeriodDisplay" label="有効期間" sortBy="rewardPeriodDisplay" />
        <DateField source="paymentStartDate" label="支払期間開始日" sortBy="paymentStartDate" />
        <FunctionField
          source="paymentEndDate"
          label="支払期間終了日"
          sortBy="paymentEndDate"
          render={(record: BuyerRecord) => {
            if (!record.paymentEndDate) {
              return <CenteredHyphen />;
            }
            const date = new Date(record.paymentEndDate);
            return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
          }}
        />
        <NumberField source="purchaseCount" label="購入回数" textAlign="left" />
        <YenField source="totalSales" label="累積売上" textAlign="left" />
        <YenField source="totalReward" label="累積報酬額" textAlign="left" />
        <FunctionField
          source="status"
          label="ステータス"
          render={(record: BuyerRecord) => (record.status === 'active' ? '有効' : '無効')}
        />
      </FlexibleListLayout>
    </List>
  );
};

function buildFilters(dashboardType: BuyerDashboardType): React.ReactElement[] {
  const filters: React.ReactElement[] = [];
  if (dashboardType === 'admin') {
    filters.push(
      <ReferenceInput key="clientId" source="clientId" reference="references/clients" label="ECサイト" alwaysOn perPage={MAX_PER_PAGE}>
        <SelectInput optionText="name" label="ECサイト" emptyText="(選択解除)" />
      </ReferenceInput>
    );
  }
  if (dashboardType === 'admin' || dashboardType === 'client') {
    filters.push(
      <ReferenceInput key="partnerId" source="partnerId" reference="references/partners" label="パートナー" alwaysOn perPage={MAX_PER_PAGE}>
        <SelectInput optionText="name" label="パートナー" emptyText="(選択解除)" />
      </ReferenceInput>
    );
  }
  filters.push(
    <DateInput
      key="startDate"
      source="startDate"
      label="開始日"
      alwaysOn
    />,
    <DateInput
      key="endDate"
      source="endDate"
      label="終了日"
      alwaysOn
    />,
    <SelectInput
      key="status"
      source="status"
      label="ステータス"
      alwaysOn
      choices={[
        { id: 'active', name: '有効' },
        { id: 'inactive', name: '無効' }
      ]}
      emptyText="(選択解除)"
    />
  );
  // perPage 選択肢は React Admin の <List perPage={...} > で適用される
  return filters;
}

function computeExcludedExportFields(dashboardType: BuyerDashboardType): Set<keyof BuyerRecord> {
  const excluded = new Set<keyof BuyerRecord>();
  if (dashboardType === 'client') {
    excluded.add('clientId');
    excluded.add('clientName');
  } else if (dashboardType === 'partner') {
    excluded.add('clientId');
    excluded.add('ecSiteClientId');
    excluded.add('partnerName');
  }
  return excluded;
}

export default BuyerList;
export { PER_PAGE_OPTIONS };
